import { type FC, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { useShopShuffleMutation } from '@/api/queries/shop';
import { useCheckVIPstatus } from '@/common/hooks/checkVIPstatus';
import { useCountDownTimer } from '@/common/hooks/count-down-timer';
import { playSound } from '@/common/lib/soundEffect';
import { timeStringFormatShortForm } from '@/common/lib/timer';
import { type BoxList } from '@/common/types/shop/marketBoxes';
import { ModalContext } from '@/components/Modals/Modal/config';
import {
  Glow,
  ShopBadge,
  ShopBuyButton,
  ShuffleButton,
} from '@/components/Shop/ui/ShopUi';
import { Picture } from '@/components/ui/Picture/Picture';
import { rewardArray } from '@/components/ui/RewardBlockItems/rewardArray';
import RewardBlockItems from '@/components/ui/RewardBlockItems/RewardBlockItems';

import { infoBox } from './BoxCard/infoBox';
import { packLook } from './config';

import css from './styles.module.scss';

import lightPng from '/img/shop/v2/card-light.png';
import lightWebp from '/img/shop/v2/card-light.webp';
import vipCrown from '/img/shop/v2/packs/vip-crown.svg';
import tgemPng from '/img/shop/v2/tgem-icon.png';
import tgemWebp from '/img/shop/v2/tgem-icon.webp';
import watchPng from '/img/watch.png';

/** Cooldown and the buy window, same for every pack. */
const usePack = (
  pack: BoxList,
  cardType: 'small' | 'big',
): { boxSeconds: number; onBuyBox: () => void } => {
  const { name, tgem, reward, nextUseInSeconds } = pack;
  const { openModal } = useContext(ModalContext);
  const boxSeconds = useCountDownTimer(
    nextUseInSeconds <= 0 ? 0 : nextUseInSeconds,
  );
  const { title, gradient } = infoBox[name];

  useEffect(() => {
    const claimFreeBoxDate = localStorage.getItem('claim-free-box-date');

    if (name === 'free' && !claimFreeBoxDate && !!nextUseInSeconds) {
      localStorage.setItem('claim-free-box-date', new Date().toISOString());
    }
  }, [name, nextUseInSeconds]);

  const onBuyBox = (): void => {
    openModal({
      renderKind: 'buyMarketBox',
      props: { name, title, gradient, reward, tgem, boxSeconds, cardType },
    });
  };

  return { boxSeconds, onBuyBox };
};

/** Free Pack / ADS Pack tile. */
const SmallPackCard: FC<BoxList> = (pack) => {
  const { t } = useTranslation();
  const statusVIP = useCheckVIPstatus();
  const { boxSeconds, onBuyBox } = usePack(pack, 'small');
  const { title, text } = infoBox[pack.name];
  const look = packLook[pack.name];
  const isFree = pack.name === 'free';
  const cooldown = boxSeconds
    ? timeStringFormatShortForm(boxSeconds * 1000)
    : undefined;

  return (
    <div className={css.smallCard}>
      <Glow
        color={isFree ? '#82E024' : '#fff'}
        blur={32}
        className="opacity-60 w-[190px] h-20 left-0 -top-[30px]"
      />
      <div
        className={cn(
          'relative flex flex-col items-center gap-2 w-full',
          !!boxSeconds && 'opacity-50',
        )}
      >
        <span className={css.smallTitle}>{t(title)}</span>
        <Picture
          img={look.png}
          webp={look.webp}
          alt=""
          className="block size-16"
        />
        {text && <p className={css.smallText}>{t(text)}</p>}
      </div>
      {cooldown ? (
        <ShopBuyButton
          variant="locked"
          label={cooldown}
          disabled
          className="!rounded-[8px]"
        />
      ) : isFree ? (
        <ShopBuyButton
          label={t('Free')}
          className="!rounded-[8px]"
          onClick={onBuyBox}
        />
      ) : (
        <button type="button" className={css.adsButton} onClick={onBuyBox}>
          {statusVIP ? (
            <span className={css.vipCircle}>
              <img src={vipCrown} alt="" className="w-4 h-3" />
            </span>
          ) : (
            <img src={watchPng} alt="" className="size-6" />
          )}
          <span>{statusVIP ? t('Free') : t('Watch')}</span>
        </button>
      )}
      {isFree && !boxSeconds && <div className={css.marker} />}
    </div>
  );
};

/** Standard / Pro / Gold / Elite pack row. */
const BigPackCard: FC<BoxList> = (pack) => {
  const { t } = useTranslation();
  const [shuffle, { isLoading }] = useShopShuffleMutation();
  const isStandard = pack.name === 'starter';
  const { boxSeconds, onBuyBox } = usePack(pack, isStandard ? 'small' : 'big');
  const { title, add } = infoBox[pack.name];
  const look = packLook[pack.name];
  const cooldown = boxSeconds
    ? timeStringFormatShortForm(boxSeconds * 1000)
    : undefined;
  const rewards = rewardArray(pack.reward);

  const buyButton = (
    <ShopBuyButton
      variant={cooldown ? 'locked' : 'purple'}
      label={cooldown ?? pack.tgem}
      disabled={!!cooldown}
      icon={
        !cooldown && (
          <Picture
            img={tgemPng}
            webp={tgemWebp}
            alt=""
            className="block size-6"
          />
        )
      }
      wrapperClassName={isStandard ? 'w-[182px] max-w-[50%]' : 'flex-1 min-w-0'}
      onClick={onBuyBox}
    />
  );

  return (
    <div className={cn(css.bigCard, isStandard && css.standard)}>
      <div className={css.bigBg}>
        {look.glow && (
          <>
            <Glow
              color={look.glow}
              className="opacity-[0.63] size-[124px] left-[calc(50%_+_120px)] -translate-x-1/2 top-[33px]"
            />
            <Glow
              color={look.glow}
              className="opacity-[0.63] size-[124px] left-[calc(50%_+_120px)] -translate-x-1/2 top-[33px]"
            />
            <Glow
              color={look.glow}
              className="opacity-[0.63] size-[225px] -right-10 top-[14px]"
            />
            <Glow
              color={look.leftGlow ?? look.glow}
              className="opacity-50 size-40 -left-20 -top-20"
            />
          </>
        )}
        <Picture
          img={lightPng}
          webp={lightWebp}
          alt=""
          className={cn(css.bigLight, isStandard && css.bigLightStandard)}
        />
        <div className={css.caseBox} style={look.artBox}>
          <Picture
            img={look.png}
            webp={look.webp}
            alt={title}
            className="absolute block max-w-none"
            style={look.artCrop}
          />
        </div>
      </div>

      {look.badge && (
        <ShopBadge
          text={t(look.badge.text)}
          color={look.badge.color}
          size="sm"
        />
      )}

      <div className="relative flex flex-col items-start gap-3">
        <span className={css.bigTitle}>{t(title)}</span>
        <div className={cn(css.rewards, !!boxSeconds && 'opacity-50')}>
          {rewards.slice(0, isStandard ? 1 : undefined).map((el, index) => (
            <RewardBlockItems key={`r${index}`} {...el} variant="medium" />
          ))}
          {/* Standard pack: a random material and resource (icons only) */}
          {isStandard &&
            add?.slice(1).map((icon) => (
              <div key={icon} className={css.addItem}>
                <img src={icon} alt="" className="w-7" />
              </div>
            ))}
          {isStandard &&
            rewards
              .slice(1)
              .map((el, index) => (
                <RewardBlockItems key={`s${index}`} {...el} variant="medium" />
              ))}
        </div>
      </div>

      <div className="relative flex items-center gap-2 w-full mt-auto">
        {!isStandard && (
          <ShuffleButton
            className={cn('flex-1 min-w-0', !!boxSeconds && 'opacity-50')}
            isLoading={isLoading}
            disabled={!!boxSeconds}
            onClick={() => {
              playSound('buttons/shuffle');
              shuffle({ type: pack.name });
            }}
          />
        )}
        {buyButton}
      </div>
    </div>
  );
};

export { BigPackCard, SmallPackCard };
